import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, ErrorBanner } from '@shop/ui'
import { formatMoment } from '../ui/format'
import { platformName, postStatusName, retryTarget, unschedulePost } from './api'
import type { SocialPost } from './api'

/**
 * النتيجةُ لكلّ منصّة — <b>مستقلّة</b>: رابطُ ما نُشر، وسببُ ما فشل، و«أعِد» له وحدَه.
 */
export function PostResults({ post, compact = false }: { post: SocialPost; compact?: boolean }) {
  const queries = useQueryClient()

  const retry = useMutation({
    mutationFn: retryTarget,
    onSuccess: () => queries.invalidateQueries({ queryKey: ['social-posts'] }),
  })

  const cancel = useMutation({
    mutationFn: unschedulePost,
    onSuccess: () => queries.invalidateQueries({ queryKey: ['social-posts'] }),
  })

  return (
    <div className="rounded-lg border border-line p-3">
      <div className="flex flex-wrap items-baseline gap-2 text-sm">
        <b>{postStatusName[post.status]}</b>
        {post.status === 'SCHEDULED' && post.scheduledAt && (
          <span className="text-xs">موعدُه {formatMoment(post.scheduledAt)}</span>
        )}
        {post.size === 'STORY' && <span className="rounded bg-surface-raised px-1.5 text-xs">ستوري</span>}
        {post.imageCount > 1 && <span className="rounded bg-surface-raised px-1.5 text-xs">ألبوم · {post.imageCount}</span>}
        <span className="text-xs text-ink-soft">{formatMoment(post.createdAt)}</span>
        {post.status === 'SCHEDULED' && (
          <Button disabled={cancel.isPending} onClick={() => cancel.mutate(post.id)}>ألغِ الجدولة</Button>
        )}
        {!compact && <span className="min-w-0 flex-1 truncate text-ink-soft">{post.caption.split('\n')[0]}</span>}
      </div>

      <ul className="mt-2 grid gap-1 text-sm">
        {post.targets.map((t) => (
          <li key={t.id} className="flex flex-wrap items-center gap-2">
            <span className="w-20 shrink-0 font-semibold">{platformName[t.platform]}</span>

            {t.status === 'PUBLISHED' && (
              <>
                <span className="text-ok">✓ نُشر</span>
                {t.permalink && (
                  <a className="underline" href={t.permalink} target="_blank" rel="noopener">افتح المنشور</a>
                )}
              </>
            )}

            {t.status === 'FAILED' && (
              <>
                <span className="text-danger">✗ فشل</span>
                <span className="min-w-0 flex-1 text-xs text-ink-soft">{t.error}</span>
                <Button disabled={retry.isPending} onClick={() => retry.mutate(t.id)}>أعِد المحاولة</Button>
              </>
            )}

            {t.status === 'PENDING' && <span className="text-ink-soft">لم يُنشر بعد</span>}
          </li>
        ))}
      </ul>

      <ErrorBanner error={retry.error ?? cancel.error} />
    </div>
  )
}
